import React, { ReactElement, cloneElement } from "react";

interface CopyToClipboardProps {
  text: string;
  children: ReactElement;
  onCopy?: (text: string, result: boolean) => void;
}

const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
  text,
  children,
  onCopy,
}) => {
  const handleCopy = async (event: React.MouseEvent) => {
    // Prevent default if it's a button or other clickable element
    event.preventDefault();

    try {
      // Use modern clipboard API if available
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        onCopy?.(text, true);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const result = document.execCommand("copy");
        document.body.removeChild(textArea);
        onCopy?.(text, result);
      }
    } catch (error) {
      console.error("Failed to copy text: ", error);
      onCopy?.(text, false);
    }
  };

  // Clone the child element and add our click handler
  // We preserve any existing onClick handler by calling it after our copy operation
  const originalOnClick = (children.props as any)?.onClick;

  const childWithProps = cloneElement(children, {
    onClick: (event: React.MouseEvent) => {
      handleCopy(event);
      // Call the original onClick if it exists
      if (originalOnClick) {
        originalOnClick(event);
      }
    },
    style: {
      cursor: "pointer",
      ...((children.props as any)?.style || {}),
    },
  } as any);

  return childWithProps;
};

export default CopyToClipboard;
export { CopyToClipboard };
